import { Injectable, inject } from '@angular/core';
import { DataService } from '../services/data.service';
import * as pasBundleGenerator from '@simpatico.ai/lib-pas-request/src/pas-bundle-generator.js';
import type { fhirR4 } from '@smile-cdr/fhirts';
import { Observable, from } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import { EndpointService } from '../services/endpoint.service';
import { ResourceType, supportedOrderTypes } from '../models/ResourceType';
import { CoverageInformationService } from '../services/coverage-information.service';
import { LoggerService } from '../services/logger.service';
import { TranslateService } from '@ngx-translate/core';

// need wrapper for testing/mocking
export abstract class PasBundleGeneratorWrapper {
  static readonly generatePasRequestBundle =
    pasBundleGenerator.generatePasRequestBundle;
  static readonly generatePasInquireRequestBundle =
    pasBundleGenerator.generatePasInquireRequestBundle;
  static readonly generateSubmitAttachmentParameters =
    pasBundleGenerator.generateSubmitAttachmentParameters;
}
@Injectable({
  providedIn: 'root',
})
export class SubmitService {
  private readonly dataService = inject(DataService);
  private readonly endpointService = inject(EndpointService);
  private readonly logger = inject(LoggerService);
  private readonly translate = inject(TranslateService);

  readonly signatureExtensionUrl =
    'http://hl7.org/fhir/StructureDefinition/questionnaireresponse-signature';
  readonly signatureTypeCoding = {
    system: 'urn:iso-astm:E1762-95:2013',
    code: '1.2.840.10065.1.12.1.5',
  };
  readonly contextExtensionUrl =
    'http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/qr-context';

  signAndSaveQr(
    qr: fhirR4.QuestionnaireResponse,
    name: string,
  ): Observable<fhirR4.QuestionnaireResponse> {
    const sign = this.createSignature(name);
    const questionnaireResponse = cloneDeep(qr);

    questionnaireResponse.status = 'completed';
    const signExt = questionnaireResponse.extension?.find(
      (ext) => ext.url === this.signatureExtensionUrl,
    );
    if (signExt) {
      signExt.valueSignature = sign.valueSignature;
    } else {
      questionnaireResponse.extension = (
        questionnaireResponse.extension || []
      ).concat(sign);
    }
    return from(
      this.dataService.updateResource(
        questionnaireResponse,
      ) as Promise<fhirR4.QuestionnaireResponse>,
    );
  }

  private createSignature(name: string) {
    return {
      url: this.signatureExtensionUrl,
      valueSignature: {
        type: [this.signatureTypeCoding],
        when: new Date().toJSON(),
        who: {
          reference: this.dataService.fhirUser,
        },
        data: btoa(name),
      },
    };
  }

  async createPasBundle(questionnaireResponse: fhirR4.QuestionnaireResponse) {
    const order = this.getOrderReference(questionnaireResponse);
    const bundle = await PasBundleGeneratorWrapper.generatePasRequestBundle(
      order,
      await this.dataService.getServerUrl(),
      await this.dataService.getAuth(),
    );
    if (bundle.warnings?.length > 0)
      this.logger.warn('Warning:\n', bundle.warnings.join('\n '));
    if (bundle.success) return bundle.bundle;
    throw bundle.messageLog;
  }

  async submitPasBundle(payload: fhirR4.Bundle | fhirR4.Parameters) {
    return await this.dataService.post<fhirR4.Bundle | fhirR4.OperationOutcome>(
      payload,
      this.endpointService.getPasSubmitUrl(),
    );
  }

  async processSubmitResponse(
    response: fhirR4.Bundle | fhirR4.OperationOutcome,
    pasBundle: fhirR4.Bundle,
  ) {
    const claimResponse = this.findResource<fhirR4.ClaimResponse>(
      response,
      ResourceType.ClaimResponse,
    );
    if (claimResponse) {
      await this.dataService.createResource(claimResponse);
      await this.addClaimIdentifierToOrder(pasBundle);
    }
  }

  /**
   * Records the PAS claim identifier on the order's first
   * `ext-coverage-information` extension, so the order links to the claim
   * submitted for it. The identifier and the order are both taken from the
   * submitted PAS request bundle. Skipped when the bundle's Claim has no
   * identifier, the bundle holds no order, or the order has no
   * `ext-coverage-information`.
   */
  private async addClaimIdentifierToOrder(pasBundle: fhirR4.Bundle) {
    const claimIdentifier = this.findResource<fhirR4.Claim>(
      pasBundle,
      ResourceType.Claim,
    )?.identifier?.at(0);
    const order = this.findResource(pasBundle, ...supportedOrderTypes);
    if (
      !claimIdentifier ||
      !order ||
      !CoverageInformationService.getPAExtensions(order).length
    ) {
      return;
    }
    await this.dataService.updateResource(
      CoverageInformationService.addOrReplaceNestedExtension(
        order,
        CoverageInformationService.pasClaimIdentifierExtensionUrl,
        'valueIdentifier',
        claimIdentifier,
      ),
    );
  }

  /**
   * The PAS claim already submitted for the order and its stored
   * ClaimResponse, both looked up on the FHIR server by `claimIdentifier`;
   * either one missing is an error.
   */
  async getSubmissionStatus(claimIdentifier: fhirR4.Identifier): Promise<{
    claim: fhirR4.Claim;
    claimResponse: fhirR4.ClaimResponse;
  }> {
    const identifierToken = encodeURIComponent(
      `${claimIdentifier.system}|${claimIdentifier.value}`,
    );
    const claim = this.findResource<fhirR4.Claim>(
      await this.dataService.getResource(
        `${ResourceType.Claim}?identifier=${identifierToken}`,
      ),
      ResourceType.Claim,
    );
    if (!claim) {
      throw new Error(
        this.translate.instant('Error.NoClaim', {
          identifier: claimIdentifier.value,
        }),
      );
    }
    const claimResponse = this.findResource<fhirR4.ClaimResponse>(
      await this.dataService.getResource(
        `${ResourceType.ClaimResponse}?identifier=${identifierToken}`,
      ),
      ResourceType.ClaimResponse,
    );
    if (!claimResponse?.id) {
      throw new Error(
        this.translate.instant('Error.NoClaimResponse', {
          identifier: claimIdentifier.value,
        }),
      );
    }
    return { claim, claimResponse };
  }

  /**
   * Inquires the payer about the status of the PAS claim already submitted
   * for the order, and overwrites the stored ClaimResponse with the payer's
   * answer. Resolves to the Claim and the updated ClaimResponse.
   */
  async inquireClaim(
    orderReference: string,
    claimIdentifier: fhirR4.Identifier,
  ): Promise<{
    claim: fhirR4.Claim;
    claimResponse: fhirR4.ClaimResponse;
  }> {
    const { claim, claimResponse: prevClaimResponse } =
      await this.getSubmissionStatus(claimIdentifier);
    const response = await this.callInquire(orderReference, claim);
    const claimResponse = await this.updateClaimResponse(
      response,
      prevClaimResponse,
    );
    return { claim, claimResponse };
  }

  private async callInquire(orderReference: string, claim: fhirR4.Claim) {
    const inquiry =
      await PasBundleGeneratorWrapper.generatePasInquireRequestBundle(
        orderReference,
        await this.dataService.getServerUrl(),
        claim,
        await this.dataService.getAuth(),
      );
    if (inquiry.warnings?.length > 0)
      this.logger.warn('Warning:\n', inquiry.warnings.join('\n '));
    if (!inquiry.success) throw inquiry.messageLog;

    return this.dataService.post<fhirR4.Bundle | fhirR4.OperationOutcome>(
      inquiry.bundle,
      this.endpointService.getPasInquireUrl(),
    );
  }

  private async updateClaimResponse(
    response: fhirR4.Bundle | fhirR4.OperationOutcome,
    prevClaimResponse: fhirR4.ClaimResponse,
  ): Promise<fhirR4.ClaimResponse> {
    const claimResponse = this.findResource<fhirR4.ClaimResponse>(
      response,
      ResourceType.ClaimResponse,
    );
    if (!claimResponse) {
      throw new Error(this.translate.instant('Error.NoInquiryClaimResponse'));
    }
    const claimResponseToStore = {
      ...claimResponse,
      id: prevClaimResponse.id,
      meta: {
        ...claimResponse.meta,
        lastUpdated:
          (response as fhirR4.Bundle).timestamp ??
          claimResponse.meta?.lastUpdated,
      },
    };
    const updated = await this.dataService.updateResource(claimResponseToStore);
    if (updated?.resourceType !== ResourceType.ClaimResponse) {
      this.logger.warn(
        'ClaimResponse update did not return a ClaimResponse; using the sent resource:',
        updated,
      );
      return claimResponseToStore;
    }
    return updated as fhirR4.ClaimResponse;
  }

  /** The first `qr-context` reference that points at a supported order type. */
  private getOrderReference(
    questionnaireResponse: fhirR4.QuestionnaireResponse,
  ): string | undefined {
    return questionnaireResponse.extension
      ?.filter((ext) => ext.url === this.contextExtensionUrl)
      .map((ext) => ext.valueReference?.reference)
      .find(
        (ref) =>
          ref && supportedOrderTypes.some((type) => ref.startsWith(type)),
      );
  }

  /** The first bundle entry resource whose type is one of `resourceTypes`. */
  private findResource<T extends fhirR4.Resource = fhirR4.Resource>(
    bundle: fhirR4.Bundle | fhirR4.OperationOutcome,
    ...resourceTypes: string[]
  ): T | undefined {
    return (bundle as fhirR4.Bundle).entry?.find(
      (entry) =>
        entry.resource && resourceTypes.includes(entry.resource.resourceType),
    )?.resource as T | undefined;
  }

  async submitAttachment(task: fhirR4.Task) {
    const params =
      await PasBundleGeneratorWrapper.generateSubmitAttachmentParameters(
        task,
        await this.dataService.getServerUrl(),
        await this.dataService.getAuth(),
      );
    if (params.created) {
      await this.dataService.post(
        params.parameters,
        this.endpointService.getCdexAttachmentUrl(),
      );
      await this.setTaskStatusToCompleted(task);
    } else throw new Error(params.messageLog);
  }

  async setTaskStatusToCompleted(task: fhirR4.Task) {
    const taskResource = cloneDeep(task);
    taskResource.status = 'completed';
    await this.dataService.updateResource(taskResource);
  }
}
